<!-- ============================================================
     FINANCE & ACCOUNTING SYSTEM
     ============================================================ -->
<div class="fin-page">

  <!-- ── TOAST ──────────────────────────────────────────── -->
  @if (toast()) {
    <div class="fin-toast alert alert-success d-flex align-items-center gap-2 shadow">
      <i class="bi bi-check-circle-fill fs-5"></i><strong>{{ toast() }}</strong>
    </div>
  }

  <!-- ══════════════════════════════════════════════════════
       FEE DETAIL VIEW MODAL
       ══════════════════════════════════════════════════════ -->
  @if (viewingFee()) {
    @let vf = viewingFee()!;
    <div class="fin-overlay" (click)="viewingFee.set(null)">
      <div class="fin-modal-lg" (click)="$event.stopPropagation()">
        <div class="fmh">
          <div class="fmh-icon"><i class="bi bi-receipt-cutoff"></i></div>
          <div><h5 class="fmh-title">Fee Receipt – {{ vf.receipt_no || 'Pending' }}</h5><p class="fmh-sub">{{ vf.student_name }} · {{ vf.class_name }}-{{ vf.section }}</p></div>
          <button class="fmh-close" (click)="viewingFee.set(null)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="fmb p-4">
          <div class="row g-3 mb-3">
            <div class="col-md-4"><div class="fi-kv"><span>Student ID</span><span>{{ vf.student_id }}</span></div></div>
            <div class="col-md-4"><div class="fi-kv"><span>Roll No.</span><span>{{ vf.roll_no }}</span></div></div>
            <div class="col-md-4"><div class="fi-kv"><span>Academic Year</span><span>{{ vf.academic_year }}</span></div></div>
            <div class="col-md-4"><div class="fi-kv"><span>Due Date</span><span>{{ formatDate(vf.due_date) }}</span></div></div>
            <div class="col-md-4"><div class="fi-kv"><span>Payment Date</span><span>{{ formatDate(vf.payment_date) }}</span></div></div>
            <div class="col-md-4"><div class="fi-kv"><span>Mode</span><span>{{ vf.payment_mode || '—' }}</span></div></div>
          </div>
          <table class="fin-table mb-3">
            <thead><tr><th>Fee Head</th><th class="text-end">Amount</th><th class="text-end">Waiver</th><th class="text-end">Paid</th><th class="text-end">Balance</th></tr></thead>
            <tbody>
              @for (item of vf.fee_items; track item.name) {
                <tr>
                  <td>{{ item.name }}</td>
                  <td class="text-end">{{ formatFull(item.amount) }}</td>
                  <td class="text-end">{{ item.waiver ? formatFull(item.waiver) : '—' }}</td>
                  <td class="text-end {{ item.paid >= item.amount ? 'text-success fw-semibold' : '' }}">{{ formatFull(item.paid) }}</td>
                  <td class="text-end {{ (item.amount - item.paid) > 0 ? 'text-danger fw-semibold' : 'text-muted' }}">{{ formatFull(item.amount - item.paid) }}</td>
                </tr>
              }
              <tr class="fee-total-row">
                <td class="fw-bold">Total</td>
                <td class="text-end fw-bold">{{ formatFull(vf.total_amount) }}</td>
                <td></td>
                <td class="text-end fw-bold text-success">{{ formatFull(vf.paid_amount) }}</td>
                <td class="text-end fw-bold {{ vf.balance > 0 ? 'text-danger' : 'text-muted' }}">{{ formatFull(vf.balance) }}</td>
              </tr>
            </tbody>
          </table>
          <div class="d-flex align-items-center gap-3">
            <span class="{{ getFeeStatusClass(vf.status) }}">{{ vf.status }}</span>
            @if (vf.bank_ref) { <span class="text-muted small">Bank Ref: {{ vf.bank_ref }}</span> }
            @if (vf.remarks)  { <span class="text-muted small">Remarks: {{ vf.remarks }}</span> }
          </div>
        </div>
        <div class="fmf">
          <button class="btn btn-outline-secondary" (click)="viewingFee.set(null)">Close</button>
          @if (vf.balance > 0) {
            <button class="btn fin-btn-primary" (click)="viewingFee.set(null); openPaymentModal(vf)"><i class="bi bi-cash me-1"></i>Record Payment</button>
          }
        </div>
      </div>
    </div>
  }

  <!-- ══════════════════════════════════════════════════════
       PAYMENT / FEE MODAL
       ══════════════════════════════════════════════════════ -->
  @if (showFeeModal()) {
    <div class="fin-overlay" (click)="showFeeModal.set(false)">
      <div class="fin-modal" (click)="$event.stopPropagation()">
        <div class="fmh fmh-fee">
          <div class="fmh-icon"><i class="bi bi-cash-coin"></i></div>
          <div><h5 class="fmh-title">{{ isPaymentMode() ? 'Record Fee Payment' : 'New Fee Entry' }}</h5>
          @if (isPaymentMode()) { <p class="fmh-sub">{{ feeForm.student_name }} · Balance: {{ formatFull(feeForm.balance ?? 0) }}</p> }
          </div>
          <button class="fmh-close" (click)="showFeeModal.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="fmb p-4">
          @if (isPaymentMode()) {
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label fw-semibold">Payment Amount <span class="req">*</span></label>
                <div class="input-group">
                  <span class="input-group-text">₹</span>
                  <input type="number" class="form-control" [(ngModel)]="feeForm.payment_amount" min="1" [class.is-invalid]="feeSubmitted && !(feeForm.payment_amount! > 0)">
                </div>
                @if (feeSubmitted && !(feeForm.payment_amount! > 0)) { <div class="text-danger small mt-1">Enter a valid payment amount.</div> }
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Payment Mode <span class="req">*</span></label>
                <select class="form-select" [(ngModel)]="feeForm.payment_mode">
                  @for (m of ['Cash','Cheque','Online','UPI','DD','NEFT']; track m) { <option [value]="m">{{ m }}</option> }
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Payment Date</label>
                <input type="date" class="form-control" [(ngModel)]="feeForm.payment_date">
              </div>
              <div class="col-12">
                <label class="form-label fw-semibold">Bank Reference / Cheque No.</label>
                <input type="text" class="form-control" [(ngModel)]="feeForm.bank_ref" placeholder="Transaction / Cheque reference">
              </div>
              <div class="col-12">
                <label class="form-label fw-semibold">Remarks</label>
                <input type="text" class="form-control" [(ngModel)]="feeForm.remarks" maxlength="200">
              </div>
            </div>
          } @else {
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label fw-semibold">Student Name <span class="req">*</span></label>
                <input type="text" class="form-control" [(ngModel)]="feeForm.student_name" placeholder="Student full name">
              </div>
              <div class="col-md-3">
                <label class="form-label fw-semibold">Class</label>
                <input type="text" class="form-control" [(ngModel)]="feeForm.class_name" placeholder="e.g. Class IX">
              </div>
              <div class="col-md-3">
                <label class="form-label fw-semibold">Section</label>
                <input type="text" class="form-control" [(ngModel)]="feeForm.section" maxlength="3">
              </div>
              <div class="col-md-4">
                <label class="form-label fw-semibold">Due Date</label>
                <input type="date" class="form-control" [(ngModel)]="feeForm.due_date">
              </div>
              <div class="col-md-4">
                <label class="form-label fw-semibold">Academic Year</label>
                <input type="text" class="form-control" [(ngModel)]="feeForm.academic_year" placeholder="2025-26">
              </div>
              <div class="col-md-4">
                <label class="form-label fw-semibold">Payment Mode</label>
                <select class="form-select" [(ngModel)]="feeForm.payment_mode">
                  @for (m of ['Cash','Cheque','Online','UPI','DD','NEFT']; track m) { <option [value]="m">{{ m }}</option> }
                </select>
              </div>
            </div>
          }
        </div>
        <div class="fmf">
          <button class="btn btn-outline-secondary px-4" (click)="showFeeModal.set(false)">Cancel</button>
          <button class="btn fin-btn-primary px-5" (click)="saveFeePayment()">
            <i class="bi bi-check-circle-fill me-1"></i>{{ isPaymentMode() ? 'Record Payment' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ══════════════════════════════════════════════════════
       EXPENSE MODAL
       ══════════════════════════════════════════════════════ -->
  @if (showExpenseModal()) {
    <div class="fin-overlay" (click)="showExpenseModal.set(false)">
      <div class="fin-modal-lg" (click)="$event.stopPropagation()">
        <div class="fmh fmh-expense">
          <div class="fmh-icon"><i class="bi bi-receipt"></i></div>
          <div><h5 class="fmh-title">{{ editingId() ? 'Edit Expense' : 'Add Expense' }}</h5></div>
          <button class="fmh-close" (click)="showExpenseModal.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="fmb p-4">
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label fw-semibold">Category <span class="req">*</span></label>
              <select class="form-select" [(ngModel)]="expForm.category" [class.is-invalid]="expSubmitted && !expForm.category?.trim()">
                <option value="">Select…</option>
                @for (c of ['Salary','Utilities','Maintenance','Academics','Sports','Transport','IT','Marketing','Others']; track c) { <option [value]="c">{{ c }}</option> }
              </select>
            </div>
            <div class="col-md-8">
              <label class="form-label fw-semibold">Description <span class="req">*</span></label>
              <input type="text" class="form-control" [(ngModel)]="expForm.description" maxlength="200" placeholder="Brief description of expense" [class.is-invalid]="expSubmitted && !expForm.description?.trim()">
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Amount (₹) <span class="req">*</span></label>
              <input type="number" class="form-control" [(ngModel)]="expForm.amount" min="1" [class.is-invalid]="expSubmitted && !(expForm.amount! > 0)">
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Date</label>
              <input type="date" class="form-control" [(ngModel)]="expForm.date">
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Payment Mode</label>
              <select class="form-select" [(ngModel)]="expForm.payment_mode">
                @for (m of ['Cash','Cheque','Online','UPI','DD','NEFT']; track m) { <option [value]="m">{{ m }}</option> }
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Account</label>
              <input type="text" class="form-control" [(ngModel)]="expForm.account_name" placeholder="Ledger account">
            </div>
            <div class="col-md-6">
              <label class="form-label fw-semibold">Vendor / Payee</label>
              <input type="text" class="form-control" [(ngModel)]="expForm.vendor" maxlength="100">
            </div>
            <div class="col-md-6">
              <label class="form-label fw-semibold">Invoice / Ref. No.</label>
              <input type="text" class="form-control" [(ngModel)]="expForm.invoice_no" maxlength="80">
            </div>
            <div class="col-12">
              <label class="form-label fw-semibold">Remarks</label>
              <textarea class="form-control" [(ngModel)]="expForm.remarks" rows="2" maxlength="300"></textarea>
            </div>
          </div>
          @if (hasExpErrors()) {
            <div class="alert alert-danger d-flex gap-2 align-items-center mt-3 py-2">
              <i class="bi bi-exclamation-triangle-fill"></i>Please fill all required fields.
            </div>
          }
        </div>
        <div class="fmf">
          <button class="btn btn-outline-secondary px-4" (click)="showExpenseModal.set(false)">Cancel</button>
          <button class="btn fin-btn-primary px-5" (click)="saveExpense()"><i class="bi bi-check-circle-fill me-1"></i>Save Expense</button>
        </div>
      </div>
    </div>
  }

  <!-- ══════════════════════════════════════════════════════
       ACCOUNT MODAL
       ══════════════════════════════════════════════════════ -->
  @if (showAccountModal()) {
    <div class="fin-overlay" (click)="showAccountModal.set(false)">
      <div class="fin-modal" (click)="$event.stopPropagation()">
        <div class="fmh fmh-account">
          <div class="fmh-icon"><i class="bi bi-diagram-3-fill"></i></div>
          <div><h5 class="fmh-title">{{ editingId() ? 'Edit Account' : 'New Account' }}</h5></div>
          <button class="fmh-close" (click)="showAccountModal.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="fmb p-4">
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label fw-semibold">Account Code <span class="req">*</span></label>
              <input type="text" class="form-control font-monospace" [(ngModel)]="accForm.code" maxlength="10" placeholder="e.g. 1001" [class.is-invalid]="accSubmitted && !accForm.code?.trim()">
            </div>
            <div class="col-md-8">
              <label class="form-label fw-semibold">Account Name <span class="req">*</span></label>
              <input type="text" class="form-control" [(ngModel)]="accForm.name" maxlength="100" [class.is-invalid]="accSubmitted && !accForm.name?.trim()">
            </div>
            <div class="col-md-4">
              <label class="form-label fw-semibold">Type</label>
              <select class="form-select" [(ngModel)]="accForm.type">
                @for (t of ['Asset','Liability','Equity','Income','Expense']; track t) { <option [value]="t">{{ t }}</option> }
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-semibold">Group</label>
              <input type="text" class="form-control" [(ngModel)]="accForm.group" maxlength="60" placeholder="e.g. Current Assets">
            </div>
            <div class="col-md-4">
              <label class="form-label fw-semibold">Opening Balance (₹)</label>
              <input type="number" class="form-control" [(ngModel)]="accForm.balance">
            </div>
            <div class="col-12">
              <label class="form-label fw-semibold">Description</label>
              <input type="text" class="form-control" [(ngModel)]="accForm.description" maxlength="200">
            </div>
          </div>
        </div>
        <div class="fmf">
          <button class="btn btn-outline-secondary px-4" (click)="showAccountModal.set(false)">Cancel</button>
          <button class="btn fin-btn-primary px-5" (click)="saveAccount()"><i class="bi bi-check-circle-fill me-1"></i>Save Account</button>
        </div>
      </div>
    </div>
  }

  <!-- ══════════════════════════════════════════════════════
       JOURNAL VIEW MODAL
       ══════════════════════════════════════════════════════ -->
  @if (viewingJournal()) {
    @let vj = viewingJournal()!;
    <div class="fin-overlay" (click)="viewingJournal.set(null)">
      <div class="fin-modal-lg" (click)="$event.stopPropagation()">
        <div class="fmh fmh-journal">
          <div class="fmh-icon"><i class="bi bi-journal-bookmark-fill"></i></div>
          <div><h5 class="fmh-title">{{ vj.entry_no }}</h5><p class="fmh-sub">{{ vj.narration }}</p></div>
          <span class="{{ getJnlStatusClass(vj.status) }} ms-auto me-3">{{ vj.status }}</span>
          <button class="fmh-close" (click)="viewingJournal.set(null)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="fmb p-4">
          <div class="row g-3 mb-3">
            <div class="col-md-4"><div class="fi-kv"><span>Date</span><span>{{ formatDate(vj.date) }}</span></div></div>
            <div class="col-md-4"><div class="fi-kv"><span>Reference</span><span>{{ vj.reference || '—' }}</span></div></div>
            <div class="col-md-4"><div class="fi-kv"><span>Created By</span><span>{{ vj.created_by }}</span></div></div>
          </div>
          <table class="fin-table">
            <thead><tr><th>Account Code</th><th>Account Name</th><th class="text-end">Debit (₹)</th><th class="text-end">Credit (₹)</th></tr></thead>
            <tbody>
              @for (ln of vj.lines; track ln.account_code) {
                <tr>
                  <td class="font-monospace small">{{ ln.account_code }}</td>
                  <td>{{ ln.account_name }}</td>
                  <td class="text-end">{{ ln.debit  ? formatFull(ln.debit)  : '' }}</td>
                  <td class="text-end">{{ ln.credit ? formatFull(ln.credit) : '' }}</td>
                </tr>
              }
              <tr class="fee-total-row">
                <td colspan="2" class="fw-bold">Total</td>
                <td class="text-end fw-bold">{{ formatFull(vj.total_debit) }}</td>
                <td class="text-end fw-bold">{{ formatFull(vj.total_credit) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="fmf">
          <button class="btn btn-outline-secondary" (click)="viewingJournal.set(null)">Close</button>
          @if (vj.status === 'Draft') {
            <button class="btn btn-success" (click)="viewingJournal.set(null); postJournal(vj.id)"><i class="bi bi-check2-all me-1"></i>Post Entry</button>
          }
        </div>
      </div>
    </div>
  }

  <!-- ══════════════════════════════════════════════════════
       JOURNAL FORM MODAL
       ══════════════════════════════════════════════════════ -->
  @if (showJournalForm()) {
    <div class="fin-overlay" (click)="showJournalForm.set(false)">
      <div class="fin-modal-xl" (click)="$event.stopPropagation()">
        <div class="fmh fmh-journal">
          <div class="fmh-icon"><i class="bi bi-journal-plus"></i></div>
          <div><h5 class="fmh-title">New Journal Entry</h5></div>
          <button class="fmh-close" (click)="showJournalForm.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="fmb p-4">
          <div class="row g-3 mb-4">
            <div class="col-md-3">
              <label class="form-label fw-semibold">Date <span class="req">*</span></label>
              <input type="date" class="form-control" [(ngModel)]="jnlForm.date" [class.is-invalid]="jnlSubmitted && !jnlForm.date">
            </div>
            <div class="col-md-6">
              <label class="form-label fw-semibold">Narration <span class="req">*</span></label>
              <input type="text" class="form-control" [(ngModel)]="jnlForm.narration" maxlength="200" placeholder="Brief description of the entry" [class.is-invalid]="jnlSubmitted && !jnlForm.narration.trim()">
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Reference</label>
              <input type="text" class="form-control" [(ngModel)]="jnlForm.reference" maxlength="80" placeholder="Voucher / Invoice no.">
            </div>
          </div>
          <table class="fin-table mb-2">
            <thead>
              <tr>
                <th style="width:140px">Acc. Code</th>
                <th>Account Name</th>
                <th class="text-end" style="width:150px">Debit (₹)</th>
                <th class="text-end" style="width:150px">Credit (₹)</th>
                <th style="width:40px"></th>
              </tr>
            </thead>
            <tbody>
              @for (ln of jnlForm.lines; track $index) {
                <tr>
                  <td><input type="text" class="form-control form-control-sm font-monospace" [(ngModel)]="ln.account_code" placeholder="e.g. 1002"></td>
                  <td><input type="text" class="form-control form-control-sm" [(ngModel)]="ln.account_name" placeholder="Account name"></td>
                  <td><input type="number" class="form-control form-control-sm text-end" [(ngModel)]="ln.debit"  min="0" placeholder="0"></td>
                  <td><input type="number" class="form-control form-control-sm text-end" [(ngModel)]="ln.credit" min="0" placeholder="0"></td>
                  <td>
                    @if (jnlForm.lines.length > 2) {
                      <button class="jnl-del-btn" (click)="removeJournalLine($index)"><i class="bi bi-trash3"></i></button>
                    }
                  </td>
                </tr>
              }
              <tr class="jnl-totals-row">
                <td colspan="2" class="fw-bold ps-3">Total</td>
                <td class="text-end fw-bold pe-2">{{ formatFull(jnlDebitTotal()) }}</td>
                <td class="text-end fw-bold pe-2">{{ formatFull(jnlCreditTotal()) }}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <div class="d-flex align-items-center gap-3 mt-1">
            <button class="btn btn-sm btn-outline-primary" (click)="addJournalLine()"><i class="bi bi-plus-circle me-1"></i>Add Line</button>
            @if (jnlSubmitted && !jnlBalanced()) {
              <span class="text-danger small fw-semibold"><i class="bi bi-exclamation-triangle-fill me-1"></i>Debit ≠ Credit — entry is not balanced.</span>
            }
            @if (jnlBalanced() && jnlDebitTotal() > 0) {
              <span class="text-success small fw-semibold"><i class="bi bi-check2 me-1"></i>Balanced</span>
            }
          </div>
        </div>
        <div class="fmf">
          <button class="btn btn-outline-secondary px-4" (click)="showJournalForm.set(false)">Cancel</button>
          <button class="btn fin-btn-primary px-5" (click)="saveJournal()"><i class="bi bi-save-fill me-1"></i>Save as Draft</button>
        </div>
      </div>
    </div>
  }

  <!-- ════════════════════════════════════════════════════════
       PAGE HEADER
       ════════════════════════════════════════════════════════ -->
  <div class="fin-ph mb-4">
    <div>
      <h1 class="fin-title"><i class="bi bi-cash-stack me-2 text-primary" style="font-size:1.1rem;"></i>Finance & Accounting</h1>
      <p class="fin-subtitle">Fee collection · Expenses · Ledger · Reports · Budget tracking</p>
    </div>
    <div class="d-flex gap-2 flex-wrap">
      @if (activeTab() === 'fees' && feeSubTab() === 'collection') {
        <button class="btn fin-btn-primary btn-sm" (click)="openNewFeeForm()"><i class="bi bi-plus-circle-fill me-1"></i>New Fee Entry</button>
      }
      @if (activeTab() === 'expenses') {
        <button class="btn fin-btn-primary btn-sm" (click)="openExpenseForm()"><i class="bi bi-plus-circle-fill me-1"></i>Add Expense</button>
      }
      @if (activeTab() === 'accounts') {
        <button class="btn fin-btn-primary btn-sm" (click)="openAccountForm()"><i class="bi bi-plus-circle-fill me-1"></i>New Account</button>
      }
      @if (activeTab() === 'journal') {
        <button class="btn fin-btn-primary btn-sm" (click)="openJournalForm()"><i class="bi bi-journal-plus me-1"></i>New Journal Entry</button>
      }
    </div>
  </div>

  <!-- TABS -->
  <div class="fin-tabs mb-4">
    @for (t of [{k:'dashboard',l:'Dashboard',ic:'bi-grid-fill'},{k:'fees',l:'Fee Management',ic:'bi-cash-coin'},{k:'expenses',l:'Expenses',ic:'bi-receipt'},{k:'accounts',l:'Chart of Accounts',ic:'bi-diagram-3-fill'},{k:'journal',l:'Journal',ic:'bi-journal-bookmark-fill'},{k:'reports',l:'Reports',ic:'bi-bar-chart-fill'}]; track t.k) {
      <button class="ftab" [class.active]="activeTab() === t.k" (click)="activeTab.set($any(t.k))">
        <i class="bi {{ t.ic }} me-1"></i>{{ t.l }}
      </button>
    }
  </div>

  <!-- ════════════════════════════════════════════════════════
       DASHBOARD
       ════════════════════════════════════════════════════════ -->
  @if (activeTab() === 'dashboard') {
    <!-- KPI Cards -->
    <div class="fin-kpi-row mb-4">
      <div class="kpi-card kpi-revenue">
        <div class="kpi-icon"><i class="bi bi-currency-rupee"></i></div>
        <div class="kpi-body">
          <div class="kpi-val">{{ formatCurrency(dashStats().revenue) }}</div>
          <div class="kpi-label">Fee Collected</div>
          <div class="kpi-sub">{{ dashStats().collPct }}% collection rate</div>
        </div>
        <div class="kpi-trend up"><i class="bi bi-arrow-up-short"></i>+8.2%</div>
      </div>
      <div class="kpi-card kpi-expense">
        <div class="kpi-icon"><i class="bi bi-receipt"></i></div>
        <div class="kpi-body">
          <div class="kpi-val">{{ formatCurrency(dashStats().expenses) }}</div>
          <div class="kpi-label">Total Expenses</div>
          <div class="kpi-sub">{{ expStats().pending }} pending approval</div>
        </div>
        <div class="kpi-trend up"><i class="bi bi-arrow-up-short"></i>+3.1%</div>
      </div>
      <div class="kpi-card kpi-surplus">
        <div class="kpi-icon"><i class="bi bi-graph-up-arrow"></i></div>
        <div class="kpi-body">
          <div class="kpi-val {{ surplusClass(dashStats().surplus) }}">{{ formatCurrency(dashStats().surplus) }}</div>
          <div class="kpi-label">Net Surplus</div>
          <div class="kpi-sub">Revenue minus paid expenses</div>
        </div>
        <div class="kpi-trend up"><i class="bi bi-check-circle-fill"></i></div>
      </div>
      <div class="kpi-card kpi-outstanding">
        <div class="kpi-icon"><i class="bi bi-hourglass-split"></i></div>
        <div class="kpi-body">
          <div class="kpi-val text-danger">{{ formatCurrency(dashStats().outstanding) }}</div>
          <div class="kpi-label">Outstanding Fees</div>
          <div class="kpi-sub">{{ feeStats().pending + feeStats().overdue }} students pending</div>
        </div>
        <div class="kpi-trend {{ feeStats().overdue > 0 ? 'down' : 'neutral' }}">
          {{ feeStats().overdue }} overdue
        </div>
      </div>
    </div>

    <div class="row g-4 mb-4">
      <!-- Monthly Bar Chart -->
      <div class="col-lg-7">
        <div class="fin-card h-100">
          <div class="fcard-header"><i class="bi bi-bar-chart-line-fill me-2"></i>Monthly Revenue vs Expenses (2026)</div>
          <div class="fcard-body">
            <div class="chart-legend mb-2">
              <span class="leg-item"><span class="leg-dot" style="background:#1a3a6b;"></span>Revenue</span>
              <span class="leg-item ms-3"><span class="leg-dot" style="background:#ef4444;"></span>Expenses</span>
            </div>
            <svg viewBox="0 0 545 175" class="fin-svg" style="width:100%;max-height:200px;">
              <!-- Y-axis labels -->
              <text x="42" y="15"  text-anchor="end" class="svg-label">1.2M</text>
              <text x="42" y="53"  text-anchor="end" class="svg-label">800K</text>
              <text x="42" y="90"  text-anchor="end" class="svg-label">400K</text>
              <text x="42" y="143" text-anchor="end" class="svg-label">0</text>
              <!-- Grid lines -->
              <line x1="45" y1="14"  x2="540" y2="14"  stroke="#f3f4f6" stroke-width="1"/>
              <line x1="45" y1="52"  x2="540" y2="52"  stroke="#f3f4f6" stroke-width="1"/>
              <line x1="45" y1="90"  x2="540" y2="90"  stroke="#f3f4f6" stroke-width="1"/>
              <line x1="45" y1="140" x2="540" y2="140" stroke="#e5e7eb" stroke-width="1.5"/>
              <!-- Bars -->
              @for (d of monthlyData; track d.month; let i = $index) {
                <rect [attr.x]="barX(i,false)" [attr.y]="barY(d.rev)" [attr.width]="barW" [attr.height]="barH(d.rev)" rx="3" fill="#1a3a6b" opacity=".85"/>
                <rect [attr.x]="barX(i,true)"  [attr.y]="barY(d.exp)" [attr.width]="barW" [attr.height]="barH(d.exp)" rx="3" fill="#ef4444" opacity=".75"/>
                <text [attr.x]="monthLblX(i)" [attr.y]="chartBaseY + 14" text-anchor="middle" class="svg-label">{{ d.month }}</text>
              }
            </svg>
          </div>
        </div>
      </div>

      <!-- Fee Status Donut -->
      <div class="col-lg-5">
        <div class="fin-card h-100">
          <div class="fcard-header"><i class="bi bi-pie-chart-fill me-2"></i>Fee Collection Status</div>
          <div class="fcard-body d-flex align-items-center justify-content-center gap-4 flex-wrap">
            <svg viewBox="0 0 160 160" style="width:160px;height:160px;flex-shrink:0;">
              <circle cx="80" cy="80" r="52" fill="none" stroke="#f3f4f6" stroke-width="22"/>
              @for (seg of donutSegments(); track seg.label) {
                <circle cx="80" cy="80" r="52" fill="none"
                  [attr.stroke]="seg.color" stroke-width="22"
                  [attr.stroke-dasharray]="seg.dash"
                  [attr.stroke-dashoffset]="seg.offset"
                  style="transform:rotate(-90deg);transform-origin:50% 50%;"/>
              }
              <text x="80" y="74" text-anchor="middle" class="svg-center-val">{{ fees().length }}</text>
              <text x="80" y="92" text-anchor="middle" class="svg-center-lbl">Students</text>
            </svg>
            <div class="donut-legend">
              <div class="dl-item"><span class="dl-dot" style="background:#059669;"></span><span class="dl-label">Paid</span><span class="dl-val">{{ feeStats().paid }}</span></div>
              <div class="dl-item"><span class="dl-dot" style="background:#d97706;"></span><span class="dl-label">Partial</span><span class="dl-val">{{ feeStats().partial }}</span></div>
              <div class="dl-item"><span class="dl-dot" style="background:#3b82f6;"></span><span class="dl-label">Pending</span><span class="dl-val">{{ feeStats().pending }}</span></div>
              <div class="dl-item"><span class="dl-dot" style="background:#ef4444;"></span><span class="dl-label">Overdue</span><span class="dl-val text-danger fw-bold">{{ feeStats().overdue }}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Activity Row -->
    <div class="row g-4">
      <div class="col-md-6">
        <div class="fin-card">
          <div class="fcard-header"><i class="bi bi-clock-history me-2"></i>Recent Fee Collections</div>
          <div class="fcard-body p-0">
            @for (f of fees().filter(x => x.paid_amount > 0).slice(0,6); track f.id) {
              <div class="recent-row">
                <div class="rr-av">{{ getInitials(f.student_name) }}</div>
                <div class="rr-info">
                  <div class="rr-name">{{ f.student_name }}</div>
                  <div class="rr-meta">{{ f.class_name }}-{{ f.section }} · {{ formatDate(f.payment_date) }}</div>
                </div>
                <div class="ms-auto text-end">
                  <div class="rr-amt text-success">+{{ formatFull(f.paid_amount) }}</div>
                  <span class="{{ getFeeStatusClass(f.status) }}">{{ f.status }}</span>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="fin-card">
          <div class="fcard-header exp-header"><i class="bi bi-receipt me-2"></i>Recent Expenses</div>
          <div class="fcard-body p-0">
            @for (e of expenses().slice(0,6); track e.id) {
              <div class="recent-row">
                <div class="rr-cat-icon"><i class="bi bi-tag-fill"></i></div>
                <div class="rr-info">
                  <div class="rr-name">{{ e.description | slice:0:42 }}{{ e.description.length > 42 ? '…' : '' }}</div>
                  <div class="rr-meta">{{ e.category }} · {{ formatDate(e.date) }}</div>
                </div>
                <div class="ms-auto text-end">
                  <div class="rr-amt text-danger">–{{ formatFull(e.amount) }}</div>
                  <span class="{{ getExpStatusClass(e.status) }}">{{ e.status }}</span>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  }

  <!-- ════════════════════════════════════════════════════════
       FEE MANAGEMENT
       ════════════════════════════════════════════════════════ -->
  @if (activeTab() === 'fees') {
    <!-- Fee Sub-tabs -->
    <div class="fee-subtabs mb-3">
      <button class="fsub-tab" [class.active]="feeSubTab() === 'collection'" (click)="feeSubTab.set('collection')">
        <i class="bi bi-list-check me-1"></i>Fee Collection
      </button>
      <button class="fsub-tab" [class.active]="feeSubTab() === 'structure'" (click)="feeSubTab.set('structure')">
        <i class="bi bi-diagram-2 me-1"></i>Fee Structure
      </button>
    </div>

    <!-- Collection Summary Cards -->
    @if (feeSubTab() === 'collection') {
      <div class="fee-summary-row mb-3">
        <div class="fsr-item"><div class="fsr-val text-success">{{ formatCurrency(feeStats().totalPaid) }}</div><div class="fsr-label">Collected</div></div>
        <div class="fsr-item"><div class="fsr-val text-danger">{{ formatCurrency(feeStats().totalBal) }}</div><div class="fsr-label">Outstanding</div></div>
        <div class="fsr-item"><div class="fsr-val">{{ feeStats().paid }}</div><div class="fsr-label">Fully Paid</div></div>
        <div class="fsr-item"><div class="fsr-val text-warning">{{ feeStats().partial }}</div><div class="fsr-label">Partial</div></div>
        <div class="fsr-item"><div class="fsr-val text-primary">{{ feeStats().pending }}</div><div class="fsr-label">Pending</div></div>
        <div class="fsr-item"><div class="fsr-val text-danger">{{ feeStats().overdue }}</div><div class="fsr-label">Overdue</div></div>
      </div>

      <!-- Filter Bar -->
      <div class="fin-card mb-3"><div class="fcard-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-5">
            <div class="input-group">
              <span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
              <input class="form-control border-start-0" placeholder="Name, receipt no., student ID…"
                [value]="feeSearch()" (input)="feeSearch.set($any($event.target).value)">
              @if (feeSearch()) { <button class="btn btn-outline-secondary" (click)="feeSearch.set('')"><i class="bi bi-x"></i></button> }
            </div>
          </div>
          <div class="col-md-2">
            <select class="form-select" [value]="feeStatus()" (change)="feeStatus.set($any($event.target).value)">
              <option value="">All Status</option>
              @for (s of ['Paid','Partial','Pending','Overdue']; track s) { <option [value]="s">{{ s }}</option> }
            </select>
          </div>
          <div class="col-md-3">
            <select class="form-select" [value]="feeClass()" (change)="feeClass.set($any($event.target).value)">
              <option value="">All Classes</option>
              @for (c of uniqueClasses(); track c) { <option [value]="c">{{ c }}</option> }
            </select>
          </div>
          <div class="col-auto">
            <button class="btn btn-outline-secondary" (click)="feeSearch.set(''); feeStatus.set(''); feeClass.set('')"><i class="bi bi-arrow-counterclockwise"></i></button>
          </div>
        </div>
        <small class="text-muted mt-2 d-block">{{ filteredFees().length }} record(s)</small>
      </div></div>

      <!-- Fee Table -->
      <div class="fin-card">
        <div class="fcard-body p-0">
          @if (filteredFees().length === 0) {
            <div class="empty-st"><i class="bi bi-cash-coin"></i><p>No fee records found</p></div>
          } @else {
            <div class="table-responsive">
              <table class="fin-table">
                <thead><tr>
                  <th>Receipt No.</th><th>Student</th><th>Class</th><th class="text-end">Total</th>
                  <th class="text-end">Paid</th><th class="text-end">Balance</th>
                  <th>Status</th><th>Due Date</th><th>Mode</th><th>Actions</th>
                </tr></thead>
                <tbody>
                  @for (f of filteredFees(); track f.id) {
                    <tr [class.row-overdue]="f.status === 'Overdue'">
                      <td class="font-monospace small">{{ f.receipt_no || '—' }}</td>
                      <td>
                        <div class="d-flex align-items-center gap-2">
                          <div class="tiny-av">{{ getInitials(f.student_name) }}</div>
                          <div>
                            <div class="small fw-semibold">{{ f.student_name }}</div>
                            <div class="tiny-meta">{{ f.student_id }}</div>
                          </div>
                        </div>
                      </td>
                      <td class="small">{{ f.class_name }}-{{ f.section }}</td>
                      <td class="text-end small">{{ formatFull(f.total_amount) }}</td>
                      <td class="text-end small text-success fw-semibold">{{ formatFull(f.paid_amount) }}</td>
                      <td class="text-end small {{ f.balance > 0 ? 'text-danger fw-semibold' : 'text-muted' }}">{{ formatFull(f.balance) }}</td>
                      <td><span class="{{ getFeeStatusClass(f.status) }}">{{ f.status }}</span></td>
                      <td class="small text-muted">{{ formatDate(f.due_date) }}</td>
                      <td class="small">{{ f.payment_mode || '—' }}</td>
                      <td>
                        <div class="d-flex gap-1">
                          <button class="act-btn view" (click)="viewFeeDetail(f)" title="View"><i class="bi bi-eye-fill"></i></button>
                          @if (f.balance > 0) {
                            <button class="act-btn pay" (click)="openPaymentModal(f)" title="Collect Payment"><i class="bi bi-cash"></i></button>
                          }
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      </div>
    }

    <!-- Fee Structure -->
    @if (feeSubTab() === 'structure') {
      <div class="row g-4">
        @for (fs of feeStructure(); track fs.id) {
          <div class="col-md-6 col-lg-4">
            <div class="fee-struct-card">
              <div class="fsc-header">
                <div class="fsc-class">{{ fs.class_name }}</div>
                <div class="fsc-year">{{ fs.academic_year }}</div>
              </div>
              <div class="fsc-body">
                @for (item of fs.items; track item.name) {
                  <div class="fsc-row">
                    <span class="fsc-name">{{ item.name }}</span>
                    <span class="fsc-amt">{{ formatFull(item.amount) }}</span>
                    <span class="fsc-due">Due: {{ formatDate(item.due_date) }}</span>
                  </div>
                }
              </div>
              <div class="fsc-footer">
                <span class="fsc-total-lbl">Annual Total</span>
                <span class="fsc-total-val">{{ formatFull(fs.total) }}</span>
              </div>
            </div>
          </div>
        }
      </div>
    }
  }

  <!-- ════════════════════════════════════════════════════════
       EXPENSES
       ════════════════════════════════════════════════════════ -->
  @if (activeTab() === 'expenses') {
    <!-- Expense Stats -->
    <div class="fin-kpi-row mb-4" style="--kpi-cols:4;">
      <div class="kpi-card kpi-expense">
        <div class="kpi-icon"><i class="bi bi-collection-fill"></i></div>
        <div class="kpi-body"><div class="kpi-val">{{ formatCurrency(expStats().total) }}</div><div class="kpi-label">Total Expenses</div></div>
      </div>
      <div class="kpi-card" style="border-top:3px solid #d97706;">
        <div class="kpi-icon" style="background:linear-gradient(135deg,#d97706,#f59e0b);"><i class="bi bi-hourglass-split"></i></div>
        <div class="kpi-body"><div class="kpi-val">{{ expStats().pending }}</div><div class="kpi-label">Pending Approval</div></div>
      </div>
      <div class="kpi-card" style="border-top:3px solid #059669;">
        <div class="kpi-icon" style="background:linear-gradient(135deg,#059669,#10b981);"><i class="bi bi-check-circle-fill"></i></div>
        <div class="kpi-body"><div class="kpi-val">{{ formatCurrency(expStats().paidAmt) }}</div><div class="kpi-label">Paid Expenses</div></div>
      </div>
      <div class="kpi-card" style="border-top:3px solid #7c3aed;">
        <div class="kpi-icon" style="background:linear-gradient(135deg,#7c3aed,#8b5cf6);"><i class="bi bi-tag-fill"></i></div>
        <div class="kpi-body"><div class="kpi-val">{{ expCategories().length }}</div><div class="kpi-label">Categories</div></div>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="fin-card mb-3"><div class="fcard-body">
      <div class="row g-3 align-items-end">
        <div class="col-md-5">
          <div class="input-group">
            <span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
            <input class="form-control border-start-0" placeholder="Description, vendor, expense no.…"
              [value]="expSearch()" (input)="expSearch.set($any($event.target).value)">
            @if (expSearch()) { <button class="btn btn-outline-secondary" (click)="expSearch.set('')"><i class="bi bi-x"></i></button> }
          </div>
        </div>
        <div class="col-md-2">
          <select class="form-select" [value]="expCat()" (change)="expCat.set($any($event.target).value)">
            <option value="">All Categories</option>
            @for (c of expCategories(); track c) { <option [value]="c">{{ c }}</option> }
          </select>
        </div>
        <div class="col-md-2">
          <select class="form-select" [value]="expStatus()" (change)="expStatus.set($any($event.target).value)">
            <option value="">All Status</option>
            @for (s of ['Pending','Approved','Paid','Rejected']; track s) { <option [value]="s">{{ s }}</option> }
          </select>
        </div>
        <div class="col-auto">
          <button class="btn btn-outline-secondary" (click)="expSearch.set(''); expCat.set(''); expStatus.set('')"><i class="bi bi-arrow-counterclockwise"></i></button>
        </div>
      </div>
      <small class="text-muted mt-2 d-block">{{ filteredExpenses().length }} record(s)</small>
    </div></div>

    <!-- Expense List -->
    @if (filteredExpenses().length === 0) {
      <div class="empty-st"><i class="bi bi-receipt-cutoff"></i><p>No expenses found</p></div>
    } @else {
      <div class="exp-list">
        @for (e of filteredExpenses(); track e.id) {
          <div class="exp-card" [class.exp-pending]="e.status === 'Pending'">
            <div class="ec-left">
              <div class="ec-cat-badge">
                <i class="bi bi-tag-fill"></i>
                <span>{{ e.category }}</span>
              </div>
            </div>
            <div class="ec-main">
              <div class="ec-row1">
                <span class="ec-no font-monospace">{{ e.expense_no }}</span>
                <span class="{{ getExpStatusClass(e.status) }} ms-1">{{ e.status }}</span>
              </div>
              <div class="ec-desc">{{ e.description }}</div>
              <div class="ec-meta">
                <span><i class="bi bi-calendar3 me-1"></i>{{ formatDate(e.date) }}</span>
                @if (e.vendor)     { <span class="ms-2"><i class="bi bi-shop me-1"></i>{{ e.vendor }}</span> }
                @if (e.invoice_no) { <span class="ms-2"><i class="bi bi-file-earmark-text me-1"></i>{{ e.invoice_no }}</span> }
                @if (e.approved_by){ <span class="ms-2"><i class="bi bi-person-check me-1"></i>{{ e.approved_by }}</span> }
                @if (e.account_name){ <span class="ms-2 text-muted">{{ e.account_name }}</span> }
              </div>
              @if (e.remarks) { <div class="ec-remark"><i class="bi bi-chat-text me-1"></i>{{ e.remarks }}</div> }
            </div>
            <div class="ec-right">
              <div class="ec-amt">{{ formatFull(e.amount) }}</div>
              <div class="ec-mode text-muted small">{{ e.payment_mode }}</div>
              <div class="ec-actions mt-2">
                @if (e.status === 'Pending') {
                  <button class="act-btn approve" (click)="approveExpense(e.id)" title="Approve"><i class="bi bi-check-lg"></i></button>
                  <button class="act-btn reject"  (click)="rejectExpense(e.id)"  title="Reject"><i class="bi bi-x-lg"></i></button>
                }
                @if (e.status === 'Approved') {
                  <button class="act-btn pay" (click)="markExpensePaid(e.id)" title="Mark Paid"><i class="bi bi-cash"></i></button>
                }
                <button class="act-btn edit" (click)="openExpenseForm(e)" title="Edit"><i class="bi bi-pencil-fill"></i></button>
                <button class="act-btn del"  (click)="deleteExpense(e.id)"  title="Delete"><i class="bi bi-trash3-fill"></i></button>
              </div>
            </div>
          </div>
        }
      </div>
    }
  }

  <!-- ════════════════════════════════════════════════════════
       CHART OF ACCOUNTS
       ════════════════════════════════════════════════════════ -->
  @if (activeTab() === 'accounts') {
    <div class="fin-card mb-3"><div class="fcard-body">
      <div class="d-flex gap-3 align-items-center flex-wrap">
        <span class="fw-semibold small text-muted">Filter by type:</span>
        @for (t of ['','Asset','Liability','Equity','Income','Expense']; track t) {
          <button class="type-filter-btn" [class.active]="accType() === t" (click)="accType.set($any(t))">
            {{ t || 'All' }}
          </button>
        }
      </div>
    </div></div>

    @for (grp of accountsByType(); track grp.type) {
      <div class="fin-card mb-3 acc-group">
        <div class="acc-group-header" [style.border-left-color]="getAccTypeColor(grp.type)">
          <div class="agh-left">
            <span class="agh-type" [style.background]="getAccTypeBg(grp.type)" [style.color]="getAccTypeColor(grp.type)">{{ grp.type }}</span>
            <span class="agh-count small text-muted">{{ grp.items.length }} accounts</span>
          </div>
          <div class="agh-total">{{ formatFull(grp.total) }}</div>
        </div>
        <div class="fcard-body p-0">
          <table class="fin-table">
            <thead><tr><th>Code</th><th>Account Name</th><th>Group</th><th>Description</th><th class="text-end">Balance</th><th>Actions</th></tr></thead>
            <tbody>
              @for (acc of grp.items; track acc.id) {
                <tr>
                  <td class="font-monospace small fw-semibold" [style.color]="getAccTypeColor(acc.type)">{{ acc.code }}</td>
                  <td class="fw-semibold small">{{ acc.name }}</td>
                  <td class="small text-muted">{{ acc.group }}</td>
                  <td class="small text-muted">{{ acc.description || '—' }}</td>
                  <td class="text-end small fw-semibold">{{ formatFull(acc.balance) }}</td>
                  <td>
                    <div class="d-flex gap-1">
                      <button class="act-btn edit" (click)="openAccountForm(acc)" title="Edit"><i class="bi bi-pencil-fill"></i></button>
                      <button class="act-btn" [class.del]="acc.is_active" [class.restore]="!acc.is_active" (click)="toggleAccount(acc.id)" [title]="acc.is_active ? 'Deactivate' : 'Activate'">
                        <i class="bi {{ acc.is_active ? 'bi-toggle-on' : 'bi-toggle-off' }}"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }
  }

  <!-- ════════════════════════════════════════════════════════
       JOURNAL / LEDGER
       ════════════════════════════════════════════════════════ -->
  @if (activeTab() === 'journal') {
    <div class="fin-card mb-3"><div class="fcard-body">
      <div class="row g-3 align-items-end">
        <div class="col-md-6">
          <div class="input-group">
            <span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
            <input class="form-control border-start-0" placeholder="Narration, entry no., reference…"
              [value]="jnlSearch()" (input)="jnlSearch.set($any($event.target).value)">
          </div>
        </div>
        <div class="col-md-3">
          <select class="form-select" [value]="jnlStatus()" (change)="jnlStatus.set($any($event.target).value)">
            <option value="">All Status</option>
            @for (s of ['Draft','Posted','Void']; track s) { <option [value]="s">{{ s }}</option> }
          </select>
        </div>
        <div class="col-auto">
          <button class="btn btn-outline-secondary" (click)="jnlSearch.set(''); jnlStatus.set('')"><i class="bi bi-arrow-counterclockwise"></i></button>
        </div>
      </div>
      <small class="text-muted mt-2 d-block">{{ filteredJournal().length }} entries</small>
    </div></div>

    @if (filteredJournal().length === 0) {
      <div class="empty-st"><i class="bi bi-journal-x"></i><p>No journal entries found</p></div>
    } @else {
      <div class="jnl-list">
        @for (j of filteredJournal(); track j.id) {
          <div class="jnl-card" [class.jnl-draft]="j.status === 'Draft'" [class.jnl-void]="j.status === 'Void'">
            <div class="jc-left">
              <div class="jc-no">{{ j.entry_no }}</div>
              <div class="jc-date">{{ formatDate(j.date) }}</div>
              <span class="{{ getJnlStatusClass(j.status) }} mt-1">{{ j.status }}</span>
            </div>
            <div class="jc-main">
              <div class="jc-narration">{{ j.narration }}</div>
              @if (j.reference) { <div class="jc-ref"><i class="bi bi-file-earmark-text me-1"></i>{{ j.reference }}</div> }
              <div class="jc-lines">
                @for (ln of j.lines.slice(0,3); track ln.account_code) {
                  <span class="jc-line-tag">
                    {{ ln.account_code }} – {{ ln.account_name }}
                    @if (ln.debit)  { <span class="dr-tag">Dr {{ formatCurrency(ln.debit)  }}</span> }
                    @if (ln.credit) { <span class="cr-tag">Cr {{ formatCurrency(ln.credit) }}</span> }
                  </span>
                }
                @if (j.lines.length > 3) { <span class="text-muted small">+{{ j.lines.length - 3 }} more…</span> }
              </div>
            </div>
            <div class="jc-right">
              <div class="jc-total">{{ formatFull(j.total_debit) }}</div>
              <div class="small text-muted">Balanced</div>
              <div class="jc-actions mt-2">
                <button class="act-btn view"  (click)="viewJournalEntry(j)" title="View"><i class="bi bi-eye-fill"></i></button>
                @if (j.status === 'Draft') {
                  <button class="act-btn approve" (click)="postJournal(j.id)"  title="Post Entry"><i class="bi bi-check2-all"></i></button>
                  <button class="act-btn del"     (click)="voidJournal(j.id)"  title="Void"><i class="bi bi-slash-circle-fill"></i></button>
                }
              </div>
            </div>
          </div>
        }
      </div>
    }
  }

  <!-- ════════════════════════════════════════════════════════
       REPORTS
       ════════════════════════════════════════════════════════ -->
  @if (activeTab() === 'reports') {
    <!-- Report type selector -->
    <div class="report-tabs mb-4">
      @for (r of [{k:'pl',l:'P&L Statement',ic:'bi-file-earmark-bar-graph-fill'},{k:'fee',l:'Fee Collection',ic:'bi-cash-coin'},{k:'expense',l:'Expense Analysis',ic:'bi-receipt'},{k:'budget',l:'Budget vs Actual',ic:'bi-speedometer2'}]; track r.k) {
        <button class="rtab" [class.active]="reportView() === r.k" (click)="reportView.set($any(r.k))">
          <i class="bi {{ r.ic }} me-1"></i>{{ r.l }}
        </button>
      }
    </div>

    <!-- P&L Report -->
    @if (reportView() === 'pl') {
      @let pl = plReport();
      <div class="row g-4">
        <div class="col-lg-6">
          <div class="fin-card">
            <div class="fcard-header income-header"><i class="bi bi-arrow-down-circle-fill me-2"></i>Income</div>
            <div class="fcard-body p-0">
              <table class="fin-table">
                <tbody>
                  <tr><td class="fw-semibold">Fee Income</td><td class="text-end text-success fw-semibold">{{ formatFull(pl.feeIncome) }}</td></tr>
                  <tr><td>Tuition Fees</td><td class="text-end">{{ formatFull(accounts().find(a=>a.code==='4001')?.balance ?? 0) }}</td></tr>
                  <tr><td>Development Fees</td><td class="text-end">{{ formatFull(accounts().find(a=>a.code==='4002')?.balance ?? 0) }}</td></tr>
                  <tr><td>Exam Fees</td><td class="text-end">{{ formatFull(accounts().find(a=>a.code==='4003')?.balance ?? 0) }}</td></tr>
                  <tr><td>Transport Fees</td><td class="text-end">{{ formatFull(accounts().find(a=>a.code==='4004')?.balance ?? 0) }}</td></tr>
                  <tr><td class="fw-semibold">Other Income</td><td class="text-end text-success fw-semibold">{{ formatFull(pl.otherIncome) }}</td></tr>
                  <tr class="fee-total-row"><td class="fw-bold">Total Income</td><td class="text-end fw-bold text-success">{{ formatFull(pl.totalIncome) }}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="fin-card">
            <div class="fcard-header exp-header"><i class="bi bi-arrow-up-circle-fill me-2"></i>Expenditure</div>
            <div class="fcard-body p-0">
              <table class="fin-table">
                <tbody>
                  @for (cat of expCategoryReport(); track cat.cat) {
                    <tr><td>{{ cat.cat }}</td><td class="text-end">{{ formatFull(cat.amt) }}</td></tr>
                  }
                  <tr class="fee-total-row"><td class="fw-bold">Total Expenditure</td><td class="text-end fw-bold text-danger">{{ formatFull(pl.expTotal) }}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <!-- Surplus / Deficit -->
        <div class="col-12">
          <div class="surplus-card {{ pl.surplus >= 0 ? 'surplus-pos' : 'surplus-neg' }}">
            <div class="sc-icon"><i class="bi {{ pl.surplus >= 0 ? 'bi-graph-up-arrow' : 'bi-graph-down-arrow' }}"></i></div>
            <div>
              <div class="sc-label">Net {{ pl.surplus >= 0 ? 'Surplus' : 'Deficit' }} (YTD)</div>
              <div class="sc-val">{{ formatFull(pl.surplus) }}</div>
            </div>
            <div class="sc-right">
              <div class="sc-sub">Total Income: {{ formatFull(pl.totalIncome) }}</div>
              <div class="sc-sub">Total Expenditure: {{ formatFull(pl.expTotal) }}</div>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Fee Collection Report -->
    @if (reportView() === 'fee') {
      <div class="fin-card">
        <div class="fcard-header"><i class="bi bi-cash-coin me-2"></i>Fee Collection – Class Wise</div>
        <div class="fcard-body p-0">
          <table class="fin-table">
            <thead><tr><th>Class</th><th class="text-center">Students</th><th class="text-end">Total Fees</th><th class="text-end">Collected</th><th class="text-end">Outstanding</th><th style="min-width:150px;">Collection %</th></tr></thead>
            <tbody>
              @for (r of feeClassReport(); track r.cls) {
                <tr>
                  <td class="fw-semibold">{{ r.cls }}</td>
                  <td class="text-center">{{ r.count }}</td>
                  <td class="text-end">{{ formatFull(r.total) }}</td>
                  <td class="text-end text-success fw-semibold">{{ formatFull(r.paid) }}</td>
                  <td class="text-end {{ r.balance > 0 ? 'text-danger fw-semibold' : 'text-muted' }}">{{ formatFull(r.balance) }}</td>
                  <td>
                    <div class="d-flex align-items-center gap-2">
                      <div class="progress flex-grow-1" style="height:8px;">
                        <div class="progress-bar {{ r.pct >= 80 ? 'bg-success' : r.pct >= 50 ? 'bg-warning' : 'bg-danger' }}" [style.width]="r.pct + '%'"></div>
                      </div>
                      <span class="small fw-semibold">{{ r.pct }}%</span>
                    </div>
                  </td>
                </tr>
              }
              <tr class="fee-total-row">
                <td class="fw-bold">Grand Total</td>
                <td class="text-center fw-bold">{{ fees().length }}</td>
                <td class="text-end fw-bold">{{ formatFull(feeStats().totalDue) }}</td>
                <td class="text-end fw-bold text-success">{{ formatFull(feeStats().totalPaid) }}</td>
                <td class="text-end fw-bold text-danger">{{ formatFull(feeStats().totalBal) }}</td>
                <td class="fw-bold">{{ dashStats().collPct }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    }

    <!-- Expense Analysis -->
    @if (reportView() === 'expense') {
      <div class="row g-4">
        <div class="col-lg-7">
          <div class="fin-card h-100">
            <div class="fcard-header exp-header"><i class="bi bi-pie-chart-fill me-2"></i>Expense Breakdown by Category</div>
            <div class="fcard-body">
              @for (cat of expCategoryReport(); track cat.cat) {
                <div class="exp-cat-row mb-3">
                  <div class="ecr-header">
                    <span class="fw-semibold small">{{ cat.cat }}</span>
                    <span class="ms-auto small fw-semibold text-danger">{{ formatFull(cat.amt) }}</span>
                    <span class="ms-2 text-muted small">{{ cat.pct }}%</span>
                  </div>
                  <div class="progress mt-1" style="height:10px;border-radius:6px;">
                    <div class="progress-bar" style="background:linear-gradient(90deg,#1a3a6b,#3b82f6);" [style.width]="expCatBarW(cat.pct)"></div>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
        <div class="col-lg-5">
          <div class="fin-card h-100">
            <div class="fcard-header exp-header"><i class="bi bi-table me-2"></i>Summary</div>
            <div class="fcard-body p-0">
              <table class="fin-table">
                <thead><tr><th>Category</th><th class="text-end">Amount</th><th class="text-end">%</th></tr></thead>
                <tbody>
                  @for (cat of expCategoryReport(); track cat.cat) {
                    <tr><td class="small">{{ cat.cat }}</td><td class="text-end small">{{ formatFull(cat.amt) }}</td><td class="text-end small">{{ cat.pct }}%</td></tr>
                  }
                  <tr class="fee-total-row"><td class="fw-bold">Total</td><td class="text-end fw-bold">{{ formatFull(expStats().total) }}</td><td class="text-end fw-bold">100%</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Budget vs Actual -->
    @if (reportView() === 'budget') {
      <div class="fin-card">
        <div class="fcard-header"><i class="bi bi-speedometer2 me-2"></i>Budget vs Actual – FY {{ budget()[0]?.year }}</div>
        <div class="fcard-body p-0">
          <table class="fin-table">
            <thead><tr><th>Category</th><th>Head</th><th class="text-end">Budgeted</th><th class="text-end">Actual</th><th class="text-end">Variance</th><th style="min-width:160px;">Utilisation</th></tr></thead>
            <tbody>
              @for (r of budgetReport(); track r.id) {
                <tr [class.budget-income]="r.category==='Income'" [class.budget-exp]="r.category==='Expense'">
                  <td><span class="bdg-cat {{ r.category === 'Income' ? 'bdg-inc' : 'bdg-exp' }}">{{ r.category }}</span></td>
                  <td class="fw-semibold small">{{ r.head }}</td>
                  <td class="text-end small">{{ formatFull(r.budgeted) }}</td>
                  <td class="text-end small fw-semibold">{{ formatFull(r.actual) }}</td>
                  <td class="text-end small fw-semibold {{ r.category === 'Income' ? (r.variance < 0 ? 'neg-val' : 'pos-val') : (r.variance < 0 ? 'neg-val' : 'pos-val') }}">
                    {{ r.variance >= 0 ? '+' : '' }}{{ formatFull(r.variance) }}
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-2">
                      <div class="progress flex-grow-1" style="height:8px;">
                        <div class="progress-bar {{ r.pct > 100 ? 'bg-danger' : r.pct > 75 ? 'bg-warning' : 'bg-success' }}" [style.width]="(r.pct > 100 ? 100 : r.pct) + '%'"></div>
                      </div>
                      <span class="small fw-semibold {{ r.pct > 100 ? 'text-danger' : '' }}">{{ r.pct }}%</span>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }
  }

</div>
